<div class="bulk-tabs">
  <button
    type="button"
    class="bulk-tab"
    data-test-id="data-capture-add-expert-upload-tab"
    [class.bulk-tab-active]="isBulkUploadStep"
    (click)="uploadStepSelected.emit()"
  >
    1. Upload files
  </button>
  <button
    type="button"
    class="bulk-tab"
    data-test-id="data-capture-add-expert-review-tab"
    [class.bulk-tab-active]="isBulkReviewStep"
    (click)="reviewStepSelected.emit()"
  >
    2. Review &amp; confirm
  </button>
</div>

@if (isBulkUploadStep) {
  <div class="bulk-body">
    <p class="bulk-hint">
      Drag in recordings and transcripts. We'll auto-pair files and flag
      anything ambiguous for review.
    </p>

    <label
      class="bulk-dropzone"
      [class.bulk-dropzone-active]="bulkDragActive"
      [class.opacity-75]="bulkStaging"
      (dragover)="bulkDragOver.emit($event)"
      (dragleave)="bulkDragLeave.emit($event)"
      (drop)="bulkDrop.emit($event)"
    >
      <input
        #bulkFileInput
        type="file"
        multiple
        accept=".m4a,.mp3,.mp4,.vtt"
        class="sr-only"
        data-test-id="data-capture-add-expert-bulk-file-input"
        (change)="emitBulkFilesChange(bulkFileInput)"
      />

      <span class="bulk-dropzone-title">
        {{
          bulkStaging
            ? 'Staging files...'
            : 'Drag files here or choose multiple files'
        }}
      </span>

      <span class="bulk-dropzone-sub"
        >Supports <code>.m4a</code>, <code>.mp3</code>, <code>.mp4</code> and
        <code>.vtt</code></span
      >
      <span class="bulk-dropzone-btn">Choose files</span>
    </label>

    <div class="bulk-step1-footer">
      <button
        type="button"
        class="bulk-link"
        data-test-id="data-capture-add-expert-without-files-button"
        (click)="addExpertWithoutFiles.emit()"
      >
        + Add expert without files
      </button>
      <button
        type="button"
        class="bulk-continue"
        data-test-id="data-capture-add-expert-continue-review-button"
        [disabled]="!rows.length"
        (click)="reviewStepSelected.emit()"
      >
        Continue to review
      </button>
    </div>
  </div>
}

@if (isBulkReviewStep) {
  <div class="bulk-body">
    <p class="bulk-hint">
      Check each row, complete any missing fields, and resolve file issues
      before confirming. Hover over file icons to see matched filenames and
      pairing details.
    </p>

    @if (unassignedFiles.length) {
      <div class="bulk-unassigned">
        <div class="bulk-unassigned-header">
          <span>Unassigned files</span>
          <button
            type="button"
            class="bulk-remove-btn"
            aria-label="Clear unassigned files"
            (click)="clearUnassigned.emit()"
          >
            &times;
          </button>
        </div>
        <ul class="bulk-unassigned-list">
          @for (item of unassignedFiles; track $index) {
            <li>{{ item.file.name }} - {{ item.reason }}</li>
          }
        </ul>
      </div>
    }

    <div class="bulk-table-wrap">
      <table class="bulk-table">
        <colgroup>
          <col style="width: 32px" />
          <col />
          <col />
          <col style="width: 130px" />
          <col style="width: 140px" />
          <col style="width: 120px" />
          <col style="width: 80px" />
          <col style="width: 80px" />
          <col style="width: 32px" />
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th>First name</th>
            <th>Last name</th>
            <th>Segment</th>
            <th>Date / time</th>
            <th>Source</th>
            <th>Recording</th>
            <th>Transcript</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track trackByRow($index, row); let i = $index) {
            <tr
              data-test-id="data-capture-add-expert-row"
              [formGroup]="row"
              [class.bulk-row-error]="hasRowUploadError(row)"
            >
              <td class="bulk-td-index">{{ i + 1 }}</td>
              <td>
                <input
                  class="bulk-input"
                  placeholder="First name*"
                  formControlName="firstName"
                  data-test-id="data-capture-add-expert-first-name-input"
                  [class.bulk-input-error]="
                    submitted && row.controls.firstName.invalid
                  "
                />
              </td>
              <td>
                <input
                  class="bulk-input"
                  placeholder="Last name*"
                  formControlName="lastName"
                  data-test-id="data-capture-add-expert-last-name-input"
                  [class.bulk-input-error]="
                    submitted && row.controls.lastName.invalid
                  "
                />
              </td>
              <td>
                <div class="bulk-segment-cell">
                  <select
                    class="bulk-input"
                    formControlName="segment"
                    data-test-id="data-capture-add-expert-segment-select"
                    [class.bulk-input-error]="
                      submitted && row.controls.segment.invalid
                    "
                  >
                    <option value="">Segment*</option>
                    @for (seg of segments; track seg.segmentId) {
                      <option [value]="seg.segmentId">
                        {{ seg.segmentName }}
                      </option>
                    }
                  </select>
                  @if (isSegmentUnoccupied(row)) {
                    <mat-icon
                      class="bulk-segment-warn"
                      matTooltip="No data sheet exists for this segment. One will need to be created before data can be viewed."
                      matTooltipPosition="above"
                    >
                      info_outline
                    </mat-icon>
                  }
                </div>
              </td>
              <td>
                <input
                  class="bulk-input"
                  type="datetime-local"
                  formControlName="dateOfCall"
                  data-test-id="data-capture-add-expert-date-of-call-input"
                  [class.bulk-input-error]="
                    submitted && row.controls.dateOfCall.invalid
                  "
                />
              </td>
              <td>
                <input
                  class="bulk-input"
                  placeholder="Source*"
                  formControlName="source"
                  data-test-id="data-capture-add-expert-source-input"
                  [class.bulk-input-error]="
                    submitted && row.controls.source.invalid
                  "
                />
              </td>
              <td class="bulk-td-file">
                <div class="bulk-cell-file">
                  @if (getTableAudioErrorMessage(row); as err) {
                    <span
                      class="bulk-cell-warn bulk-cell-clickable"
                      data-test-id="data-capture-add-expert-audio-error"
                      matTooltipPosition="above"
                      [matTooltip]="err + ' — click to replace'"
                      (click)="audioInput.click()"
                    >
                      <mat-icon class="bulk-cell-icon">error_outline</mat-icon>
                    </span>
                  } @else if (row.controls.m4aFile.value) {
                    <span
                      matTooltipPosition="above"
                      [class]="getFileStatusClass(row)"
                      [class.bulk-cell-clickable]="
                        row.controls.matchStatus.value === 'needsReview'
                      "
                      [matTooltip]="getFileTooltip(row, 'audio')"
                      (click)="
                        row.controls.matchStatus.value === 'needsReview' &&
                          audioInput.click()
                      "
                    >
                      <mat-icon class="bulk-cell-icon">{{
                        getFileStatusIcon(row)
                      }}</mat-icon>
                    </span>
                  } @else {
                    <button
                      type="button"
                      class="bulk-cell-attach"
                      data-test-id="data-capture-add-expert-audio-attach-button"
                      (click)="audioInput.click()"
                    >
                      attach
                    </button>
                  }
                  @if (row.controls.m4aFile.value) {
                    <button
                      type="button"
                      class="bulk-cell-remove-file"
                      aria-label="Remove recording file"
                      matTooltip="Remove file"
                      matTooltipPosition="above"
                      (click)="removeFile(row, 'audio')"
                    >
                      <mat-icon class="bulk-cell-icon">close</mat-icon>
                    </button>
                  }
                </div>
                <input
                  #audioInput
                  type="file"
                  accept=".m4a,.mp4,.mp3"
                  class="sr-only"
                  data-test-id="data-capture-add-expert-audio-file-input"
                  (change)="onFileChange(audioInput, row, 'audio')"
                />
              </td>
              <td class="bulk-td-file">
                <div class="bulk-cell-file">
                  @if (getTableTranscriptErrorMessage(row); as err) {
                    <span
                      class="bulk-cell-warn bulk-cell-clickable"
                      data-test-id="data-capture-add-expert-transcript-error"
                      matTooltipPosition="above"
                      [matTooltip]="err + ' — click to replace'"
                      (click)="transcriptInput.click()"
                    >
                      <mat-icon class="bulk-cell-icon">error_outline</mat-icon>
                    </span>
                  } @else if (row.controls.transcriptFile.value) {
                    <span
                      matTooltipPosition="above"
                      [class]="getFileStatusClass(row)"
                      [class.bulk-cell-clickable]="
                        row.controls.matchStatus.value === 'needsReview'
                      "
                      [matTooltip]="getFileTooltip(row, 'transcript')"
                      (click)="
                        row.controls.matchStatus.value === 'needsReview' &&
                          transcriptInput.click()
                      "
                    >
                      <mat-icon class="bulk-cell-icon">{{
                        getFileStatusIcon(row)
                      }}</mat-icon>
                    </span>
                  } @else {
                    <button
                      type="button"
                      class="bulk-cell-attach"
                      data-test-id="data-capture-add-expert-transcript-attach-button"
                      (click)="transcriptInput.click()"
                    >
                      attach
                    </button>
                  }
                  @if (row.controls.transcriptFile.value) {
                    <button
                      type="button"
                      class="bulk-cell-remove-file"
                      aria-label="Remove transcript file"
                      matTooltip="Remove file"
                      matTooltipPosition="above"
                      (click)="removeFile(row, 'transcript')"
                    >
                      <mat-icon class="bulk-cell-icon">close</mat-icon>
                    </button>
                  }
                </div>
                <input
                  #transcriptInput
                  type="file"
                  accept=".vtt"
                  class="sr-only"
                  data-test-id="data-capture-add-expert-transcript-file-input"
                  (change)="onFileChange(transcriptInput, row, 'transcript')"
                />
              </td>
              <td>
                <button
                  type="button"
                  class="bulk-remove-btn"
                  aria-label="Remove expert row"
                  data-test-id="data-capture-add-expert-remove-row-button"
                  matTooltip="Remove row"
                  matTooltipPosition="above"
                  (click)="removeRow(i)"
                >
                  &times;
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <button
      type="button"
      class="bulk-link"
      data-test-id="data-capture-add-expert-add-row-button"
      (click)="addRow()"
    >
      + Add another expert
    </button>
  </div>
}
